import React, {useState} from 'react'
import {Accordion} from '../../../../shared/Accordion'
import {useUserFeatures} from '../../../../../context/user-features-context'
import {useUserRoles} from '../../../../../utils/use-user-roles'
import {shouldUseLegacyPartnerOpsFlow} from '../../../../../utils/account-management-navigation'
import {isRestrictedAccountCreationCountry} from '../../../AccountInfo/AccountInfo'
import {
  AccountIcon,
  AccountInfo,
  AccountName,
  AccountEmailRow,
  AccountEmail,
  AccountCode,
  AccountMeta,
  LocationTag,
  RowActions,
  LocationsHeader,
  LocationsLabel,
  LocationsCount,
  AddLocationButton,
  LocationList,
  LocationRow,
  LocationPin,
  LocationName,
  LocationCity,
  LocationMeta,
  SubsCount,
  PayBadge,
  LocationActions,
  StatusBadge,
  LocationTitle,
  LocationTitleWrap,
  LocationEmailRow,
  LocationEmail,
  LocationCode,
  LocationListTitle,
  MetaDivider,
} from './AccountRow.styles'
import {Button} from '../../../../shared/Button'
import {resolveLocationAccountId} from '../../locationIdentity'
import {
  StyledBuildingIcon,
  StyledFeaturesIcon,
  StyledLocationPinIcon,
  StyledManageIcon,
  StyledLinkIcon,
  StyledViewIcon,
} from '../../../../shared/icons/icons.styles'

const isSelfPay = billTo =>
  ['self', 'child', 'self-pay', 'self pay'].includes(
    String(billTo || '')
      .trim()
      .toLowerCase(),
  )

// Location body

function LocationsBody({
  locations,
  canAddLocation = false,
  onAddLocation,
  onViewSubscription,
  onOrder,
  onEditChild,
  orderingThryvId,
  showStatus = false,
  isSandboxContext = false,
  onAddSandbox,
  onEditSandbox,
}) {
  return (
    <>
      <LocationsHeader>
        <LocationTitle>
          <LocationsLabel>
            {isSandboxContext ? 'SANDBOXES' : 'LOCATIONS'}
          </LocationsLabel>
          <LocationsCount>
            <span>{locations?.length.toString().padStart(2, '0')}</span>
          </LocationsCount>
        </LocationTitle>
        {isSandboxContext ? (
          <AddLocationButton
            type="button"
            onClick={e => {
              e.stopPropagation()
              onAddSandbox?.()
            }}
          >
            + Add Sandbox
          </AddLocationButton>
        ) : (
          canAddLocation && (
            <AddLocationButton
              type="button"
              onClick={e => {
                e.stopPropagation()
                onAddLocation?.()
              }}
            >
              + Add Location
            </AddLocationButton>
          )
        )}
      </LocationsHeader>

      <LocationList>
        {locations?.map((loc, idx) => {
          const billTo = isSelfPay(loc?.billTo) ? 'self' : 'parent'
          const locThryvId = resolveLocationAccountId(loc)
          const isOrdering = orderingThryvId && orderingThryvId === locThryvId
          return (
            <LocationRow key={loc.id ?? idx}>
              <LocationPin aria-hidden="true">
                <StyledLocationPinIcon />
              </LocationPin>

              <LocationTitleWrap>
                <LocationListTitle>
                  {loc.name && <LocationName>{loc.name}</LocationName>}

                  {(loc.address?.city || loc.city) && (
                    <LocationCity>
                      {loc.address?.city || loc.city}
                      {','}
                    </LocationCity>
                  )}
                  {(loc.address?.region || loc?.stateCode) && (
                    <LocationCity>
                      {loc.address?.region || loc?.stateCode}
                    </LocationCity>
                  )}
                </LocationListTitle>
                {(loc.email || loc.accountEmail || locThryvId) && (
                  <LocationEmailRow>
                    {(loc.email || loc.accountEmail) && (
                      <LocationEmail>
                        {loc.email || loc.accountEmail}
                      </LocationEmail>
                    )}
                    {(loc.email || loc.accountEmail) && locThryvId && (
                      <MetaDivider aria-hidden="true" />
                    )}
                    {locThryvId && <LocationCode>{locThryvId}</LocationCode>}
                  </LocationEmailRow>
                )}
              </LocationTitleWrap>

              <LocationMeta>
                {loc.subs != null && <SubsCount>{loc.subs} subs</SubsCount>}

                {showStatus && loc.linkStatus && (
                  <StatusBadge status={loc.linkStatus}>
                    {loc.linkStatus === 'pending'
                      ? 'Link Active'
                      : loc.linkStatus === 'expired'
                      ? 'Link Expired'
                      : 'Link Completed'}
                  </StatusBadge>
                )}

                {!isSandboxContext && (
                  <PayBadge billTo={billTo}>
                    {billTo === 'self' ? 'Self Pay' : 'Parent Pay'}
                  </PayBadge>
                )}

                <LocationActions>
                  <Button
                    variant="secondary-gray-reg"
                    size="sm"
                    onClick={e => {
                      e.stopPropagation()
                      onViewSubscription?.(loc)
                    }}
                  >
                    <StyledFeaturesIcon />
                    <span>Subscription Details</span>
                  </Button>

                  <Button
                    variant="secondary-blue"
                    size="sm"
                    onClick={e => {
                      e.stopPropagation()
                      isSandboxContext
                        ? onEditSandbox?.(loc)
                        : onEditChild?.(loc)
                    }}
                  >
                    <StyledManageIcon />
                    Edit
                  </Button>

                  {!isSandboxContext && (
                    <Button
                      variant="solid-dark-blue"
                      size="sm"
                      disabled={!!orderingThryvId}
                      onClick={e => {
                        e.stopPropagation()
                        onOrder?.(loc)
                      }}
                      style={
                        isOrdering ? {opacity: 0.7, cursor: 'wait'} : undefined
                      }
                    >
                      {isOrdering ? (
                        <>
                          <span
                            style={{
                              display: 'inline-block',
                              width: 12,
                              height: 12,
                              border: '2px solid currentColor',
                              borderTopColor: 'transparent',
                              borderRadius: '50%',
                              animation: 'spin 0.7s linear infinite',
                            }}
                          />
                          Loading…
                          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
                        </>
                      ) : (
                        <>
                          <StyledLinkIcon />
                          Create Purchase Link
                        </>
                      )}
                    </Button>
                  )}
                </LocationActions>
              </LocationMeta>
            </LocationRow>
          )
        })}
      </LocationList>
    </>
  )
}

// AccountBadge

function AccountBadge({
  account,
  onViewDetails,
  onToggle,
  onEdit,
  isPre5,
  onOrder,
  onOrderForParent,
  hasParentPayChild = false,
  showStatus = false,
  isSandboxContext = false,
}) {
  const company =
    account?.company ||
    account?.companyName ||
    [account?.firstName, account?.lastName].filter(Boolean).join(' ') ||
    ''
  const {locations = []} = account
  const locationCount = locations.length
  const accountEmail = account?.email || account?.accountEmail
  const accountCode = resolveLocationAccountId(account)

  return (
    <AccountMeta>
      <AccountIcon>
        <StyledBuildingIcon />
      </AccountIcon>

      <AccountInfo>
        <AccountName>{company}</AccountName>
        {(accountEmail || accountCode) && (
          <AccountEmailRow>
            {accountEmail && <AccountEmail>{accountEmail}</AccountEmail>}
            {accountEmail && accountCode && <MetaDivider aria-hidden="true" />}
            {accountCode && <AccountCode>{accountCode}</AccountCode>}
          </AccountEmailRow>
        )}
      </AccountInfo>

      {isPre5 ? (
        <LocationTag aria-label="Pre-5.0 account">
          <span>Pre-5.0</span>
        </LocationTag>
      ) : isSandboxContext ? (
        <LocationTag
          aria-label={`${locationCount} sandbox${
            locationCount !== 1 ? 'es' : ''
          }`}
        >
          <StyledLocationPinIcon />
          <span>{String(locationCount).padStart(2, '0')}</span>
          <span>{locationCount === 1 ? 'Sandbox' : 'Sandboxes'}</span>
        </LocationTag>
      ) : (
        <LocationTag
          aria-label={`${locationCount} location${
            locationCount !== 1 ? 's' : ''
          }`}
        >
          <StyledLocationPinIcon />
          <span>{String(locationCount).padStart(2, '0')}</span>
          <span>{locationCount === 1 ? 'loc' : 'locs'}</span>
        </LocationTag>
      )}

      {/* Pre-5.0 accounts render no expandable location rows, so their outstanding
          purchase-link status is surfaced here at the account level (5.0 accounts use
          the per-location badge in LocationsBody instead). */}
      {showStatus && isPre5 && account?.linkStatus && (
        <StatusBadge status={account.linkStatus}>
          {account.linkStatus === 'pending'
            ? 'Link Active'
            : account.linkStatus === 'expired'
            ? 'Link Expired'
            : 'Link Completed'}
        </StatusBadge>
      )}

      <RowActions>
        <Button
          variant="secondary-blue"
          size="sm"
          onClick={e => {
            e.stopPropagation()
            onViewDetails?.()
          }}
          aria-label={`View details for ${company}`}
        >
          <StyledViewIcon />
          <span>View Details</span>
        </Button>

        <Button
          variant="secondary-blue"
          size="sm"
          onClick={e => {
            e.stopPropagation()
            onEdit?.()
          }}
          aria-label={`Edit ${company}`}
        >
          <StyledManageIcon />
          Edit
        </Button>

        {!isSandboxContext && isPre5 && (
          <Button
            variant="solid-dark-blue"
            size="sm"
            onClick={e => {
              e.stopPropagation()
              onOrder?.(account)
            }}
            aria-label={`Create purchase link for ${company}`}
          >
            <StyledLinkIcon />
            Create Purchase Link
          </Button>
        )}

        {!isSandboxContext && !isPre5 && hasParentPayChild && (
          <Button
            variant="solid-dark-blue"
            size="sm"
            onClick={e => {
              e.stopPropagation()
              onOrderForParent?.(account)
            }}
            aria-label={`Create purchase link for ${company}`}
          >
            <StyledLinkIcon />
            Create Purchase Link
          </Button>
        )}
      </RowActions>
    </AccountMeta>
  )
}

// AccountRow

export function AccountRow({
  account,
  onViewDetails,
  onEdit,
  onEditChild,
  onAddLocation,
  onViewSubscription,
  onOrder,
  onOrderForParent,
  showStatus = false,
  orderingThryvId,
  defaultOpen = false,
  isSandboxContext = false,
  onAddSandbox,
  onEditSandbox,
}) {
  const {locations = []} = account
  const isPre5 = !!account.isPre5
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const {Enable_International_Account_Creation} = useUserFeatures()
  const {roles} = useUserRoles()
  // Only the legacy PartnerOps rollback (per-location /order/new links) lacks
  // the combined parent-pay wizard — see shouldUseLegacyPartnerOpsFlow.
  const isCoreOoeFlow = !shouldUseLegacyPartnerOpsFlow(roles)

  // A combined purchase link only makes sense when there's more than one
  // parent-pay location to combine — a single (or no) parent-pay child is
  // already covered by that location's own per-location button in
  // LocationsBody, which always targets a single location regardless of
  // billTo. Also gated to the shared wizard only — see isCoreOoeFlow.
  const parentPayChildCount = isPre5
    ? 0
    : locations.filter(l => !isSelfPay(l?.billTo ?? 'parent')).length
  const hasParentPayChild = parentPayChildCount > 1 && isCoreOoeFlow

  // GEP-6957: hide + Add Location for restricted (unsupported-country)
  // accounts — same flag/gate AccountInfo.js uses to block new-account
  // creation for these countries. Enable_International_Account_Creation only
  // widens the supported set to the PoC/Loqate countries, it isn't a blanket
  // bypass, so this is no longer a separate `|| Enable_International...` OR —
  // see that file's isRestrictedAccountCreationCountry comment for the full
  // rationale.
  const isRestrictedCountry = isRestrictedAccountCreationCountry(
    account.country || account.address?.country || account.countryCode,
    Enable_International_Account_Creation,
  )
  const canAddLocation = account.canAddLocation === true && !isRestrictedCountry

  return (
    <Accordion
      isOpen={isOpen}
      onToggle={() => setIsOpen(o => !o)}
      badge={
        <AccountBadge
          account={account}
          onViewDetails={onViewDetails}
          onToggle={() => setIsOpen(o => !o)}
          onEdit={onEdit}
          isPre5={isPre5}
          onOrder={onOrder}
          onOrderForParent={onOrderForParent}
          hasParentPayChild={hasParentPayChild}
          showStatus={showStatus}
          isSandboxContext={isSandboxContext}
        />
      }
      noTopBorder={false}
      hideChevron={isPre5}
      disabled={isPre5}
    >
      {!isPre5 && (
        <LocationsBody
          locations={locations}
          canAddLocation={canAddLocation}
          onAddLocation={onAddLocation}
          onViewSubscription={onViewSubscription}
          onOrder={onOrder}
          onEditChild={onEditChild}
          orderingThryvId={orderingThryvId}
          showStatus={showStatus}
          isSandboxContext={isSandboxContext}
          onAddSandbox={onAddSandbox}
          onEditSandbox={onEditSandbox}
        />
      )}
    </Accordion>
  )
}
